import React, { useEffect, useMemo, useState } from "react";
import BuyGame from "../../components/Modal/BuyGame";
import { useQuery } from "@tanstack/react-query";
import { getPackageListAPI, getPackageVatPercentageAPI, getPurchaseHistoryAPI } from "../../api/package";
import { useTranslation } from "react-i18next";
import Loader from "../../components/Loader/Loader";
import CmsBanner from "../../components/Banner/CmsBanner";
import PlayBtn from "../../assets/images/game-board/play-btn.svg";
import ResponsiveFlexGrid from "../../components/FlexGrid/ResponsiveFlexGrid";
import type { PaymentStatus } from "./PurchaseHistory";
import PaymentStatusModal from "../../components/Modal/PaymentStatusModal";
import { useNavigate } from "react-router-dom";
import { formatPrice } from "../../utils/functions";

const PackageList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;

  const [paymentModal, setPaymentModal] = useState<{
    isOpen: boolean;
    status: PaymentStatus;
    packageId?: string | null;
  } | null>(null);
  // Parse redirect params from backend: ?status=success|failed&package_id=...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawStatus = (params.get("status") || "").trim().toLowerCase();
    const packageId = params.get("package_id") || params.get("packageId") || params.get("package") || null;

    const status: PaymentStatus | null =
      rawStatus === "success" || rawStatus === "succeeded" ? "success" : rawStatus === "failed" || rawStatus === "failure" ? "failed" : null;

    if (!status) return;

    // ensure we are on the first page to see latest purchase

    setPaymentModal({ isOpen: true, status, packageId });
  }, [location.search]);

  const shouldFetchPackageList = !!paymentModal?.isOpen && !!paymentModal?.packageId;
  const { data: packageListData, isLoading: isPackageListLoading } = useQuery({
    queryKey: ["packageListForPaymentModal"],
    queryFn: getPackageListAPI,
    enabled: shouldFetchPackageList,
  });
  const {
    data: purchaseHistory,
    isLoading: isPurchaseHistoryLoading,
    isError: isPurchaseHistoryError,
    error: purchaseHistoryError,
  } = useQuery({
    queryKey: ["purchaseHistory"],
    queryFn: () => getPurchaseHistoryAPI(),
  });

  const purchaseHistoryItems = purchaseHistory?.data ?? [];
  const packageFromHistory = useMemo(() => {
    const packageId = paymentModal?.packageId;
    if (!packageId) return null;
    const match = purchaseHistoryItems.find((h: any) => h?.package_id?.id === packageId || h?.package_id?._id === packageId);
    return match?.package_id || null;
  }, [purchaseHistoryItems, paymentModal?.packageId]);

  const purchaseRecordForModal = useMemo(() => {
    const packageId = paymentModal?.packageId;
    if (!packageId) return null;
    return purchaseHistoryItems.find((h: any) => h?.package_id?.id === packageId || h?.package_id?._id === packageId) || null;
  }, [purchaseHistoryItems, paymentModal?.packageId]);

  const packageFromList = useMemo(() => {
    const packageId = paymentModal?.packageId;
    if (!packageId) return null;
    const list = packageListData?.data || [];
    return list.find((p: any) => p?.id === packageId || p?._id === packageId) || null;
  }, [packageListData?.data, paymentModal?.packageId]);

  const modalPackage = packageFromList || packageFromHistory;

  const handleBuyGame = (game) => {
    setSelectedGame(game);
    setIsModalOpen(true);
  };

  // always use usequery to get the package list
  const {
    data: packageList,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["packageList"],
    queryFn: getPackageListAPI,
  });

  const { data: packageVatData } = useQuery({
    queryKey: ["packageVatPercentage"],
    queryFn: getPackageVatPercentageAPI,
  });

  const vatPercentage = Number(packageVatData?.data?.vat_percentage ?? 0);
  const getPriceWithVat = (price: number | string) => {
    const basePrice = Number(price ?? 0);
    if (Number.isNaN(basePrice)) return "0.0";
    const total = basePrice + (basePrice * vatPercentage) / 100;
    return formatPrice(total);
  };

  const packageListWithVat = useMemo(() => {
    const list = packageList?.data ?? [];
    return list.map((pkg: any) => ({
      ...pkg,
      display_price: getPriceWithVat(pkg?.price),
    }));
  }, [packageList?.data, vatPercentage]);

  if (isLoading) {
    return (
      <div className="wrapper flex flex-col flex-1">
        <CmsBanner title={t("packageList.packageGame")} description={t("packageList.packageGameDescription")} />
        <section
          className="2xl:py-[3.5vw] lg:py-15 py-10 relative z-10 bg-cover bg-center bg-no-repeat bg-white"

        >
          <div className="w-full  2xl:px-[0.833vw] xl:px-10 px-4 relative z-20">
            <div className="flex items-center justify-center py-20">
              <Loader />
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="wrapper flex flex-col flex-1">
        <CmsBanner title={t("packageList.packageGame")} description={t("packageList.packageGameDescription")} />
        <section
          className="2xl:py-[3.5vw] lg:py-15 py-10 relative z-10 bg-cover bg-center bg-no-repeat bg-white"

        >
          <div className="w-full  2xl:px-[0.833vw] xl:px-10 px-4 relative z-20">
            <div className="flex items-center justify-center py-20">
              <p className="text-black-300 font-bold text-center">
                {(error as any)?.message || "Something went wrong"}
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const handleClosePaymentModal = () => {
    setPaymentModal(null);
    // Remove query params so modal doesn't reopen on refresh/back
    navigate("/package-list", { replace: true });
  };

  const getBgColor = (index: number) => {
    return ["bg-[#FFECA6]", "bg-[#FDD8AC]", "bg-[#FCD08F]", "bg-[#FBBE88]", "bg-[#FFECA6]", "bg-[#FDD8AC]", "bg-[#FCD08F]", "bg-[#FBBE88]"][index % 5];
  };

  return (
    <>
      <div className="wrapper flex flex-col flex-1 bg-primary-ivory">
        <CmsBanner title={t("packageList.packageGame")} description={t("packageList.packageGameDescription")} />

        <section
          className="2xl:py-[3.5vw] lg:py-15 py-10 relative z-10 bg-cover bg-center bg-no-repeat "

        >
          <div className="w-full  2xl:px-[0.833vw] xl:px-10 px-4 relative z-20">
            {packageListWithVat?.length > 0 ? (
              <ResponsiveFlexGrid
                items={packageListWithVat}
                maxColumns={{
                  xs: 2,
                  sm: 3,
                  md: 4,
                  lg: 5,
                }}
                gap={{
                  default: "1.5rem",
                  lg: "1.25vw",
                }}
                renderItem={(pkg, index) => {
                  const isMostPopular = index === packageListWithVat.length - 2;
                  const isBestValue = index === packageListWithVat.length - 1;

                  return (
                    <div
                      key={pkg.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleBuyGame(pkg)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleBuyGame(pkg);
                        if (e.key === " ") {
                          e.preventDefault();
                          handleBuyGame(pkg);
                        }
                      }}
                      className="relative h-full flex flex-col justify-center bg-primary-ivory rounded-lg 2xl:rounded-[0.52vw] border 2xl:border-[0.06vw] border-black-300 p-2 2xl:p-[0.52vw] gap-y-1.5 2xl:gap-y-[0.39vw] cursor-pointer transition-transform hover:-translate-y-0.5"
                    >
                      {(isMostPopular || isBestValue) && (
                        <div className="absolute -top-4 left-[-6px] z-10 rotate-[-12deg] drop-shadow-[0px_2px_0px_#110706] w-[140px] h-[45px] pointer-events-none">
                          <svg width="100%" height="100%" viewBox="0 0 140 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                            {/* Fold triangle */}
                            <path d="M 0 38 L 8 45 L 8 38 Z" fill="#8B1E19" strokeWidth="1.5" strokeLinejoin="round" />
                            {/* Ribbon body */}
                            <path d="M 0 4 L 135 4 L 122 21 L 135 38 L 0 38 Z" fill="#E1281A" strokeWidth="2" strokeLinejoin="round" />
                          </svg>
                          {/* Text overlaid */}
                          <div className="absolute top-[4px] left-[8px] right-[18px] bottom-[7px] flex items-center justify-center text-white font-bold text-[10px] sm:text-xs 2xl:text-[0.7vw] uppercase tracking-wider font-garamond">

                            {isBestValue ? t("packageList.bestValue") : t("packageList.mostPopular")}
                          </div>
                        </div>
                      )}

                      <div className={`h-[211px] w-full relative border-1 border-black-300 rounded-md ${getBgColor(index)}`}>
                        <div className="absolute inset-0 flex flex-col items-center justify-center px-3">
                          <h3 className="2xl:text-[1.95vw] md:text-3xl text-2xl md:leading-8 leading-7 text-black-300 font-bold text-center">
                            {pkg.name[currentLanguage as keyof typeof pkg.name]}
                          </h3>
                          <div className="mt-2 flex flex-col items-center gap-0.5">
                            <p className="text-black-300 text-base lg:text-lg 2xl:text-[1.17vw] font-bold">
                              {
                                pkg.games_count === 1 ? t("packageList.oneGame") : `${pkg.games_count} ${t("packageList.games")}`
                              }
                            </p>
                            <p className="text-black-300 text-base lg:text-lg 2xl:text-[1.17vw] font-bold">{pkg.display_price} BHD</p>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBuyGame(pkg);
                        }}
                        className="flex justify-center cursor-pointer transition-shadow relative"
                      >
                        <img src={PlayBtn} className="w-full" />
                        <p className="absolute text-center left-0 right-0 top-1/2 -translate-y-1/2 text-base lg:text-lg 2xl:text-[1.17vw] font-bold text-black-300">
                          {t("packageList.purchase")}
                        </p>
                      </button>
                    </div>
                  );
                }}
                cardClassName=""
              />
            ) : (
              <div className="flex w-full justify-center items-center">
                <span className="text-black-300 text-xl font-bold text-center">{t("packageList.noPackagesFound")}</span>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Buy Package Modal */}
      <BuyGame isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} game={selectedGame} />


      {!!paymentModal?.isOpen && (
        <PaymentStatusModal
          isOpen={paymentModal.isOpen}
          status={paymentModal.status}
          packageId={paymentModal.packageId}
          pkg={modalPackage}
          purchase={purchaseRecordForModal}
          isLoadingPackage={isPackageListLoading && !packageFromHistory && isPurchaseHistoryLoading}
          onClose={handleClosePaymentModal}
        />
      )}

    </>
  );
};

export default PackageList;
